import {
  type Filters,
  getResultFilterTitle,
  getStatusFilterTitle,
  Mtds,
  type PlanSummary,
  type SampleStatusEnum,
  useGetMatricesQuery,
  useGetOwnerNameQuery,
  useGetPlanSummariesQuery,
  getProducerSearchChipLabel,
  useGetProductsQuery,
  useGetSampntTypes,
  useGetSubstancesQuery
} from 'prove-common'

type SampleFilterChipsProps = {
  filters: Filters<SampleStatusEnum>
  setFilters: React.Dispatch<React.SetStateAction<Filters<SampleStatusEnum>>>
  isFiltering: boolean
}

const hasOrgFilter = ({
  regionId,
  avdelingId,
  seksjonId,
  teamId
}: Filters<SampleStatusEnum>) =>
  (regionId?.length ?? 0) > 0 ||
  (avdelingId?.length ?? 0) > 0 ||
  (seksjonId?.length ?? 0) > 0 ||
  (teamId?.length ?? 0) > 0

function formatDate(dateString: string) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

export const SampleFilterChips = ({
  filters,
  setFilters,
  isFiltering
}: SampleFilterChipsProps) => {
  const {
    planIds,
    productIds,
    matrixIds,
    sampntTypeIds,
    substanceIds,
    status,
    result,
    producerSearch,
    ownerId,
    regionId,
    avdelingId,
    seksjonId,
    teamId
  } = filters
  const { data: plans, isPending: isPlansPending } = useGetPlanSummariesQuery(
    !!planIds?.length
  )
  const { data: products, isPending: isProductsPending } = useGetProductsQuery(
    !!productIds?.length
  )
  const { data: matrices, isPending: isMatricesPending } = useGetMatricesQuery(
    !!matrixIds?.length
  )
  const { data: sampntTypes, isPending: isSampntTypesPending } =
    useGetSampntTypes(!!sampntTypeIds?.length)
  const { data: substances, isPending: isSubstancesPending } =
    useGetSubstancesQuery(!!substanceIds?.length)
  const { data: ownerName, isPending: isOwnerNamePending } =
    useGetOwnerNameQuery(ownerId ?? undefined)
  const { data: regionName, isPending: isRegionNamePending } =
    useGetOwnerNameQuery(regionId?.length ? `region:${regionId}` : undefined)
  const { data: avdelingName, isPending: isAvdelingNamePending } =
    useGetOwnerNameQuery(
      avdelingId?.length ? `avdeling:${avdelingId}` : undefined
    )
  const { data: seksjonName, isPending: isSeksjonNamePending } =
    useGetOwnerNameQuery(seksjonId?.length ? `seksjon:${seksjonId}` : undefined)
  const { data: teamName, isPending: isTeamNamePending } = useGetOwnerNameQuery(
    teamId?.length ? `team:${teamId}` : undefined
  )

  if (!isFiltering) return null

  return (
    <Mtds.Flex>
      {status?.map((statusId) => (
        <Mtds.Chip
          key={statusId}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              status: status.filter((id) => id !== statusId)
            })
          }
        >
          {getStatusFilterTitle(statusId)}
        </Mtds.Chip>
      ))}

      {!!result && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, result: undefined })}
        >
          {getResultFilterTitle(result)}
        </Mtds.Chip>
      )}

      {producerSearch?.trim() && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, producerSearch: undefined })}
        >
          {getProducerSearchChipLabel(producerSearch)}: {producerSearch.trim()}
        </Mtds.Chip>
      )}

      {planIds?.map((planId) => {
        const label =
          plans?.find((plan: PlanSummary) => `${plan.id}` === planId)?.name ??
          `Plan ID: ${planId}`
        return (
          <Mtds.Chip
            key={planId}
            data-removable={!isPlansPending}
            onClick={() =>
              setFilters({
                ...filters,
                planIds: planIds.filter((id) => id !== planId)
              })
            }
          >
            <span className='max-w-64 truncate'>
              {isPlansPending ? <Mtds.Skeleton className='w-8' /> : label}
            </span>
          </Mtds.Chip>
        )
      })}

      {productIds?.map((productId) => {
        const label =
          products?.find(({ id }) => `${id}` === productId)?.name ??
          `Produkt ID: ${productId}`
        return (
          <Mtds.Chip
            key={productId}
            data-removable={!isProductsPending}
            onClick={() =>
              setFilters({
                ...filters,
                productIds: productIds.filter((id) => id !== productId)
              })
            }
          >
            {isProductsPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}

      {matrixIds?.map((matrixId) => {
        const label =
          matrices?.find(({ id }) => `${id}` === matrixId)?.name ??
          `Matrix ID: ${matrixId}`
        return (
          <Mtds.Chip
            key={matrixId}
            data-removable={!isMatricesPending}
            onClick={() =>
              setFilters({
                ...filters,
                matrixIds: matrixIds.filter((id) => id !== matrixId)
              })
            }
          >
            {isMatricesPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}

      {substanceIds?.map((substanceId) => {
        const label =
          substances?.find(({ id }) => `${id}` === substanceId)?.name ??
          `Substans ID: ${substanceId}`
        return (
          <Mtds.Chip
            key={substanceId}
            data-removable={!isSubstancesPending}
            onClick={() =>
              setFilters({
                ...filters,
                substanceIds: substanceIds.filter((id) => id !== substanceId)
              })
            }
          >
            {isSubstancesPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}

      {filters.fromDate && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, fromDate: undefined })}
        >
          Fra: {formatDate(filters.fromDate)}
        </Mtds.Chip>
      )}

      {filters.toDate && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, toDate: undefined })}
        >
          Til: {formatDate(filters.toDate)}
        </Mtds.Chip>
      )}

      {sampntTypeIds?.map((sampntTypeId) => {
        const label =
          sampntTypes?.find(({ id }) => `${id}` === sampntTypeId)?.nameNo ??
          `Uttakssted ID: ${sampntTypeId}`
        return (
          <Mtds.Chip
            key={sampntTypeId}
            data-removable={!isSampntTypesPending}
            onClick={() =>
              setFilters({
                ...filters,
                sampntTypeIds: sampntTypeIds.filter((id) => id !== sampntTypeId)
              })
            }
          >
            {isSampntTypesPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}

      {ownerId && !hasOrgFilter(filters) && (
        <Mtds.Chip
          data-removable={!isOwnerNamePending}
          onClick={() => setFilters({ ...filters, ownerId: undefined })}
        >
          {isOwnerNamePending ? (
            <Mtds.Skeleton className='w-8' />
          ) : (
            (ownerName?.name ?? ownerId)
          )}
        </Mtds.Chip>
      )}

      {regionId && (
        <Mtds.Chip
          data-removable={!isRegionNamePending}
          onClick={() =>
            setFilters({
              ...filters,
              ownerId: undefined,
              regionId: undefined,
              avdelingId: undefined,
              seksjonId: undefined,
              teamId: undefined
            })
          }
        >
          {isRegionNamePending ? (
            <Mtds.Skeleton className='w-8' />
          ) : (
            (regionName?.name ?? regionId)
          )}
        </Mtds.Chip>
      )}

      {avdelingId && (
        <Mtds.Chip
          data-removable={!isAvdelingNamePending}
          onClick={() =>
            setFilters({
              ...filters,
              ownerId: undefined,
              avdelingId: undefined,
              seksjonId: undefined,
              teamId: undefined
            })
          }
        >
          {isAvdelingNamePending ? (
            <Mtds.Skeleton className='w-8' />
          ) : (
            (avdelingName?.name ?? avdelingId)
          )}
        </Mtds.Chip>
      )}

      {seksjonId && (
        <Mtds.Chip
          data-removable={!isSeksjonNamePending}
          onClick={() =>
            setFilters({
              ...filters,
              ownerId: undefined,
              seksjonId: undefined,
              teamId: undefined
            })
          }
        >
          {isSeksjonNamePending ? (
            <Mtds.Skeleton className='w-8' />
          ) : (
            (seksjonName?.name ?? seksjonId)
          )}
        </Mtds.Chip>
      )}

      {teamId && (
        <Mtds.Chip
          data-removable={!isTeamNamePending}
          onClick={() =>
            setFilters({ ...filters, ownerId: undefined, teamId: undefined })
          }
        >
          {isTeamNamePending ? (
            <Mtds.Skeleton className='w-8' />
          ) : (
            (teamName?.name ?? teamId)
          )}
        </Mtds.Chip>
      )}
    </Mtds.Flex>
  )
}
